/**
 * Garder son emploi et lancer son activité.
 *
 * LE SEUL OUTIL DE LA RUBRIQUE QUI PARLE À QUELQU'UN QUI N'A PAS ENCORE SAUTÉ.
 * Les vingt-neuf autres supposent une entreprise déjà créée, ou une création
 * décidée. Celui-ci répond à la question d'avant, celle du salarié qui apprend
 * le soir : est-ce que j'ai le droit, en gardant mon poste ?
 *
 * DEUX MONDES QUI SE LISENT PARTOUT COMME S'ILS N'EN FAISAIENT QU'UN.
 *   - Dans le PRIVÉ, le cumul est libre. Aucune autorisation, aucune
 *     déclaration. Deux limites seulement : la loyauté, et une clause
 *     d'exclusivité qui, justement, ne joue pas.
 *   - Dans le PUBLIC, l'interdiction est le principe (L123-1 du code général de
 *     la fonction publique), et tout passe par une autorisation ou une
 *     déclaration.
 * Une page qui mélange les deux fait renoncer un agent qui avait une voie
 * ouverte, ou lance un salarié dans une démarche qui n'existe pas.
 *
 * LA CLAUSE D'EXCLUSIVITÉ EST INOPPOSABLE PENDANT UN AN, et c'est le point que
 * presque tous écrivent de travers. On lit partout qu'il faut « vérifier que le
 * contrat n'en contient pas », comme si elle fermait la porte. L'article
 * L1222-5 dit le contraire : elle ne peut pas être opposée pendant un an à qui
 * crée ou reprend une entreprise, même stipulation contraire. Ce qui reste,
 * c'est la loyauté, que le même article rappelle, et une échéance à un an dont
 * personne ne parle.
 *
 * TROIS REFUS, écrits dans docs/glossaries/micro-entreprise/releve-cumul-emploi-2026.md :
 * ce module ne dit pas s'il faut prévenir son employeur, ne juge pas une
 * facture adressée à son propre employeur, et n'invente rien sur ce qui suit la
 * première année.
 */

/** D'où viennent les règles, et quand elles ont été relevées. */
export const SOURCES = {
    releveLe: "2026-09-23",
    revueAvantLe: "2027-03-01",
    salarie: "entreprendre.service-public.gouv.fr, cumuler les statuts de salarié et de micro-entrepreneur, vérifiée le 10 avril 2026",
    agent: "service-public.gouv.fr, cumul d'un emploi public et d'une activité privée, recoupée par le portail de la fonction publique",
};

/** La levée de la clause d'exclusivité, en mois. Article L1222-5 du code du travail. */
export const EXCLUSIVITE_LEVEE_MOIS = 12;

/** Agent public : quotité minimale pour créer une entreprise, en pourcentage d'un temps plein. */
export const QUOTITE_MINIMALE = 50;

/** Agent public : durée de l'autorisation, et son renouvellement, en années. */
export const AUTORISATION_ANNEES = 3;
export const AUTORISATION_RENOUVELLEMENT_ANNEES = 1;
/** Délai à respecter avant une nouvelle autorisation, en années. */
export const NOUVELLE_AUTORISATION_ANNEES = 3;

/**
 * Agent public : seuil sous lequel une déclaration écrite suffit.
 *
 * 70 % de la durée légale, soit 24 heures 30 par semaine. Les deux s'écrivent,
 * parce qu'un agent connaît son temps de travail en heures et son statut en
 * pourcentage, et que personne ne fait la conversion de tête.
 */
export const SEUIL_DECLARATION_POURCENT = 70;
export const SEUIL_DECLARATION_HEURES = 24.5;

/** Délais de réponse de l'administration, en mois. */
export const DELAI_ACCESSOIRE_MOIS = 1;
export const DELAI_ACCESSOIRE_COMPLEMENT_MOIS = 2;
export const DELAI_TEMPS_PARTIEL_MOIS = 2;

/**
 * Les activités que le régime micro n'accepte pas, quel que soit l'emploi.
 *
 * Elles ferment le sujet avant même la question du cumul : inutile de vérifier
 * une clause d'exclusivité pour une activité qui ne peut pas être exercée en
 * micro-entreprise.
 */
export const ACTIVITES_EXCLUES = [
    "tva-immobiliere",
    "agricole",
    "autre-caisse",
];

/** Les questions du parcours, dans l'ordre. Le résultat n'existe qu'au complet. */
export const QUESTIONS = {
    commun: ["employeur"],
    prive: ["temps", "exclusivite", "concurrence", "moment"],
    public: ["quotite", "nature"],
};

/** Les quatre verdicts. Aucun n'est un feu vert sans réserve. */
export const VERDICTS = ["ouvert", "conditionne", "ferme", "hors-sujet"];

/**
 * Ce qui manque pour conclure.
 *
 * Un diagnostic partiel dirait « vous pouvez » à quelqu'un qui n'a pas encore
 * dit qu'il visait les clients de son employeur : tant qu'une question de sa
 * branche reste ouverte, le module ne rend rien.
 */
export function valider(r) {
    const attendues = [...QUESTIONS.commun];
    if (r.employeur === "prive") attendues.push(...QUESTIONS.prive);
    if (r.employeur === "public") attendues.push(...QUESTIONS.public);

    const manques = attendues.filter((cle) => !r[cle]);
    return { manques, complet: manques.length === 0 };
}

/**
 * Le verdict, et tout ce qui le motive.
 *
 * Rend `null` tant qu'une réponse manque. Les motifs sont des clés, jamais des
 * phrases : les deux langues vivent dans le composant, comme partout dans la
 * rubrique.
 */
export function verdict(r) {
    const { complet } = valider(r);
    if (!complet) return null;

    if (r.employeur === "aucun") {
        return { statut: "hors-sujet", motifs: ["sansEmployeur"], demarches: [], alertes: [] };
    }

    if (r.employeur === "public") return verdictAgent(r);
    return verdictSalarie(r);
}

/**
 * Le salarié du privé.
 *
 * L'ordre des motifs est celui de la gravité, et il compte : la loyauté passe
 * avant la clause d'exclusivité, parce qu'une activité concurrente reste
 * fautive quand bien même aucune clause n'existe.
 */
function verdictSalarie(r) {
    const motifs = [];
    const alertes = [];

    // 1. La concurrence ferme le sujet, seule et sans appel.
    if (r.concurrence === "oui") {
        return {
            statut: "ferme",
            motifs: ["concurrenceInterdite", "loyautePermanente"],
            demarches: [],
            alertes: ["requalificationSiEmployeurClient"],
        };
    }

    // 2. Travailler pendant ses heures ou avec le matériel de l'employeur est
    //    le second motif de faute grave, et il ne se répare pas par une clause.
    if (r.moment === "pendant") {
        return {
            statut: "ferme",
            motifs: ["horsTempsDeTravail", "loyautePermanente"],
            demarches: ["revoirOrganisation"],
            alertes: [],
        };
    }

    if (r.concurrence === "inconnu") alertes.push("concurrenceADeterminer");

    // 3. La clause d'exclusivité, qui ne ferme jamais la première année.
    if (r.temps === "partiel") {
        motifs.push("exclusiviteInopposableTempsPartiel");
        if (r.exclusivite === "oui") alertes.push("clauseATempsPartielAVerifier");
    } else if (r.exclusivite === "oui") {
        motifs.push("exclusiviteLeveeUnAn");
        alertes.push("echeanceUnAn");
    } else if (r.exclusivite === "inconnu") {
        motifs.push("exclusiviteLeveeUnAn");
        alertes.push("relireLeContrat");
    } else {
        motifs.push("aucuneClause");
    }

    motifs.push("aucuneAutorisation", "loyautePermanente", "dureesMaximalesNonApplicables");

    return {
        statut: alertes.length ? "conditionne" : "ouvert",
        motifs,
        demarches: ["declarerActivite"],
        alertes,
    };
}

/**
 * L'agent public.
 *
 * Aucune branche ne rend « ouvert » : dans le public, une activité privée
 * lucrative passe toujours par une autorisation ou par une déclaration. Le
 * meilleur cas reste donc « sous condition », et c'est la vérité.
 */
function verdictAgent(r) {
    const motifs = ["interdictionDePrincipe"];
    const demarches = [];
    const alertes = [];

    if (r.quotite === "reduit") {
        motifs.push("declarationSuffit");
        demarches.push("declarationEcrite");
    } else if (r.nature === "entreprise") {
        motifs.push("tempsPartielExige");
        demarches.push("demandeTempsPartiel", "autorisationHierarchique");
        alertes.push("dureeAutorisation", "delaiAvantNouvelleDemande");
    } else {
        motifs.push("activiteAccessoire");
        demarches.push("autorisationHierarchique");
        alertes.push("delaiReponse");
    }

    if (r.nature === "vente-biens") motifs.push("venteBiensEnMicro");
    motifs.push("horsHeuresDeService");

    return { statut: "conditionne", motifs, demarches, alertes };
}

/** Le rappel des réponses, pour que le lecteur voie sur quoi le verdict repose. */
export function recapitulatif(r) {
    const { complet } = valider(r);
    if (!complet) return null;

    const lignes = ["employeur"];
    if (r.employeur === "prive") lignes.push(...QUESTIONS.prive);
    if (r.employeur === "public") lignes.push(...QUESTIONS.public);
    return lignes.map((cle) => [cle, r[cle]]);
}

/**
 * La date à laquelle la clause d'exclusivité redevient opposable.
 *
 * Elle court à partir de la création, et c'est l'échéance dont aucun article ne
 * parle : douze mois plus tard, la clause reprend ses droits, et le sujet
 * redevient entier.
 */
export function echeanceExclusivite(depart) {
    if (!depart) return null;
    const d = new Date(depart);
    if (Number.isNaN(d.getTime())) return null;
    const echeance = new Date(d);
    echeance.setMonth(echeance.getMonth() + EXCLUSIVITE_LEVEE_MOIS);
    return echeance.toISOString().slice(0, 10);
}
